import {
  Box,
  Button,
  Collapse,
  Grid,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Tooltip,
  useTheme,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import * as React from "react";
import { useContext } from "react";
import { hasPermission, isComponentDisabledForUpdates } from "../../../../../login/login_utils";
import { renderCommissioningStatusIcons } from "../enum/commissioning_required_action";
import { useUserPrivilegeKeyPermissionsMap } from "../../../../../../hooks/use_backend";
import { COMMISSIONING_WIZARD_REDUCER } from "../hooks/commissioning_wizard_reducer";
import { COMMISSIONING_PROCEDURE_ACTIONS } from "../enum/commissioning_procedure_actions";
import { CommissioningStateContext } from "../hooks/use_commissioning_state_changes";
import Loader from "../../../../../../components/general/loader";
import { toLocaleDateString } from "../../../../../../scripts/utils";
import Privilege from "../../../../../../enums/privilege_enum";
import { COMMISSIONING_PHASE_KEYS } from "../enum/commissioning_phase_keys";

export default function PhaseNavigationPanel({ required_privilege }) {
  const theme = useTheme();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const {
    commissioningState,
    dispatchCommissioningStateChanges,
    startResetMutation,
    isLoadingCommissioning,
    isStartResetMutating,
    isHardResetMutating,
  } = useContext(CommissioningStateContext);

  const handleToggle = (phase) => {
    if (phase.phase_key !== commissioningState.selectedPhase.phase_key) {
      dispatchCommissioningStateChanges({ type: COMMISSIONING_WIZARD_REDUCER.SELECT_PHASE, payload: phase });
    }
  };

  function startButton() {
    const title = "Start the procedure for all devices ready for commissioning";
    const buttonText = "Start new procedure";
    const disabledForUpdates = isComponentDisabledForUpdates(permissionMap, required_privilege.getPrivilegeKey());
    const isAdmin = hasPermission(permissionMap, Privilege.ADMIN_OVERVIEW.getPrivilegeKey(), false);
    return (
      <>
        <Box position="relative">
          <Tooltip title={title}>
            <Button
              disabled={
                commissioningState.commissioningData.has_devices_in_commissioning ||
                disabledForUpdates ||
                (commissioningState.validationState.start_reset_disabled &&
                  !(isAdmin && commissioningState.validationState.has_uncommissioned_devices))
              }
              variant="outlined"
              color="inherit"
              size="large"
              fullWidth
              sx={{ borderRadius: "2", cursor: "pointer" }}
              onClick={() => {
                if (!commissioningState.validationState.commissioning_ongoing) {
                  if (commissioningState.validationState.start_reset_disabled) {
                    const confirmed = window.confirm(
                      "Are you sure you want to start the procedure even when there are validation errors?",
                    );
                    if (confirmed) {
                      startResetMutation({ action_type: COMMISSIONING_PROCEDURE_ACTIONS.START });
                    }
                  } else {
                    startResetMutation({ action_type: COMMISSIONING_PROCEDURE_ACTIONS.START });
                  }
                }
              }}
            >
              {buttonText}
            </Button>
          </Tooltip>
        </Box>
      </>
    );
  }

  function isSelectedDevice(device) {
    return commissioningState.selectedDeviceId === device.device_id;
  }

  function isSelectedFinalVerificationStep(step) {
    return commissioningState.selectedFinalVerificationStepId === step.step_id;
  }

  function isExpandable(phase) {
    return (
      (Array.isArray(phase.devices) && phase.devices.length > 0) ||
      (Array.isArray(phase.steps) && phase.steps.length > 0)
    );
  }

  function getDeviceCollapse(isSelected, groupedDevicesPerType) {
    return (
      <Collapse in={isSelected} timeout="auto" unmountOnExit>
        {Object.entries(groupedDevicesPerType).map(([deviceType, devices]) => (
          <Box key={deviceType} sx={{ pl: 4, py: 0 }}>
            <ListItem sx={{ pl: 2, py: 0 }}>
              <ListItemText
                primary={deviceType}
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              />
            </ListItem>
            {devices.map((device, itemIndex) => (
              <ListItem key={itemIndex} sx={{ pl: 6, py: 0 }}>
                <ListItemText
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    },
                  }}
                  secondary={device.device_name}
                  secondaryTypographyProps={{
                    fontSize: "0.9rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: isSelectedDevice(device) ? "bold" : "normal", // Highlight selected device
                    color: isSelectedDevice(device) ? "primary.main" : "inherit", // Change color if selected
                  }}
                  onClick={() =>
                    dispatchCommissioningStateChanges({
                      type: COMMISSIONING_WIZARD_REDUCER.SELECT_DEVICE_ID,
                      payload: {
                        device_id: device.device_id,
                        device_db_id: device.device_db_id,
                      },
                    })
                  }
                />
                {renderCommissioningStatusIcons(device.required_action)}
              </ListItem>
            ))}
          </Box>
        ))}
      </Collapse>
    );
  }

  function getFinalVerificationCollapse(isSelected, steps) {
    return (
      <Collapse in={isSelected} timeout="auto" unmountOnExit>
        {steps.map((step, stepindex) => (
          <ListItem key={stepindex} sx={{ pl: 6, py: 0 }}>
            <ListItemText
              sx={{
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                },
              }}
              secondary={"Procedure: " + toLocaleDateString(step.date_created)}
              secondaryTypographyProps={{
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: isSelectedFinalVerificationStep(step) ? "bold" : "normal", // Highlight selected device
                color: isSelectedFinalVerificationStep(step) ? "primary.main" : "inherit", // Change color if selected
              }}
              onClick={() => {
                dispatchCommissioningStateChanges({
                  type: COMMISSIONING_WIZARD_REDUCER.SELECT_FINAL_VERIFICATION,
                  payload: {
                    finalVerificationStepId: step.step_id,
                  },
                });
              }}
            />
            {renderCommissioningStatusIcons(step.required_action)}
          </ListItem>
        ))}
      </Collapse>
    );
  }

  return commissioningState.commissioningData && Array.isArray(commissioningState.commissioningData.phases) ? (
    <>
      <Grid item xs={12} md={3}>
        {commissioningState.commissioningData.phases.map((phase, index) => {
          const isSelected = commissioningState?.selectedPhase?.phase_key === phase?.phase_key;
          const verification_steps =
            isSelected && Array.isArray(phase.steps) && phase.steps.length > 0 ? phase.steps : [];
          const groupedDevicesPerType =
            isSelected && Array.isArray(phase.devices) && phase.devices.length > 0
              ? phase.devices.reduce((acc, device) => {
                  acc[device.device_type] = acc[device.device_type] || [];
                  acc[device.device_type].push(device);
                  return acc;
                }, {})
              : {};

          return (
            <Paper
              key={index}
              sx={{
                borderRadius: "5px",
                borderWidth: "1px",
                boxShadow: 1,
                marginBottom: "10px",
                borderStyle: "solid",
                backgroundColor: isSelected ? theme.palette.background.default : theme.palette.background.border,
                borderColor: isSelected ? theme.palette.primary.main : theme.palette.tertiary.main,
              }}
            >
              <ListItemButton onClick={() => handleToggle(phase)}>
                {isExpandable(phase) &&
                  (isSelected ? (
                    <ExpandLessIcon sx={{ color: "primary.main" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: "primary.main" }} />
                  ))}
                <ListItemText
                  primary={phase.phase_title}
                  primaryTypographyProps={{
                    fontWeight: "bold",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flexGrow: 1,
                  }}
                  sx={{ marginRight: 1 }}
                />
                {phase?.phase_key !== COMMISSIONING_PHASE_KEYS.DEVICE_OVERVIEW
                  ? renderCommissioningStatusIcons(phase.required_action)
                  : null}
              </ListItemButton>
              {getDeviceCollapse(isSelected, groupedDevicesPerType)}
              {getFinalVerificationCollapse(isSelected, verification_steps)}
            </Paper>
          );
        })}
        {startButton()}
      </Grid>
    </>
  ) : isLoadingCommissioning || isStartResetMutating || isHardResetMutating ? (
    <Grid item xs={12} md={3}>
      <Paper
        sx={{
          Height: "50vh",
          borderRadius: "5px",
          borderWidth: "1px",
          boxShadow: 1,
          marginBottom: "10px",
          borderStyle: "solid",
          backgroundColor: theme.palette.background.border,
          borderColor: theme.palette.tertiary.main,
        }}
      >
        <Loader fill={true} sx={{ padding: "5px" }} />
      </Paper>
    </Grid>
  ) : null;
}
